import { errorlog } from '@/libs/error-logs';
import { toError } from '@/utils/errors';

import { ENDPOINTS } from './endpoints';
import { GET, POST, DELETE } from './base';
import { Site } from './sites';

type NPIAddress = {
  country_code: string;
  country_name: string;
  address_purpose: string; // e.g., "LOCATION", "MAILING"
  address_type: string; // e.g., "DOM"
  address_1: string;
  city: string;
  state: string;
  postal_code: string;
  telephone_number?: string;
  fax_number?: string;
};

type NPITaxonomy = {
  code: string; // e.g., "332B00000X"
  taxonomy_group: string; // often empty
  desc: string; // e.g., "Durable Medical Equipment & Medical Supplies"
  state: string | null;
  license: string | null;
  primary: boolean;
};

type NPIIdentifier = {
  code: string; // e.g., "05" for MEDICAID
  desc: string; // e.g., "MEDICAID"
  issuer: string | null;
  identifier: string; // actual identifier value
  state?: string; // e.g., "VA"
};

type NPIOtherName = {
  organization_name: string;
  type: string; // e.g., "Doing Business As"
  code: string; // e.g., "3"
};

type Npi1Provider = {
  created_epoch: string; // e.g., "1160502006000"
  enumeration_type: 'NPI-1'; // literal to distinguish from NPI-2
  last_updated_epoch: string; // e.g., "1205434788000"
  number: string; // e.g., "1275621096"
  addresses: NPIAddress[];
  basic: {
    first_name: string;
    last_name: string;
    middle_name?: string;
    credential?: string; // e.g., "DPT"
    sole_proprietor: 'YES' | 'NO';
    gender?: 'M' | 'F';
    enumeration_date: string; // e.g., "2006-10-10"
    last_updated: string; // e.g., "2008-03-13"
    status: string; // e.g., "A"
    name_prefix?: string; // e.g., "--"
    name_suffix?: string; // e.g., "--"
  };
  taxonomies: NPITaxonomy[];
  identifiers: NPIIdentifier[];
};

type Npi2Provider = {
  created_epoch: string; // e.g., "1152268924000"
  enumeration_type: 'NPI-2'; // literal to distinguish from NPI-1
  last_updated_epoch: string; // e.g., "1220458748000"
  number: string; // e.g., "1144252537"
  addresses: NPIAddress[];
  basic: {
    organization_name: string;
    organizational_subpart: 'YES' | 'NO';
    enumeration_date: string; // e.g., "2006-07-07"
    last_updated: string; // e.g., "2008-09-03"
    status: string; // e.g., "A"
    authorized_official_first_name?: string;
    authorized_official_last_name?: string;
    authorized_official_telephone_number?: string;
    authorized_official_title_or_position?: string;
    authorized_official_name_prefix?: string; // e.g., "--"
    authorized_official_name_suffix?: string; // e.g., "--"};
  };
  taxonomies: NPITaxonomy[];
  identifiers: NPIIdentifier[];
  other_names: NPIOtherName[];
};

export type NpiProvider = Npi1Provider | Npi2Provider;

export type NpiProviderPreferred = {
  site_id: string;
  preferred_npi_data: NpiProvider[];
  preferred_npi_list: Site['preferred_npi'];
};

type NpiProvidersResponse = {
  result_count: number;
  results: NpiProvider[];
};

export type GetNpiProvidersParams = {
  state: string;
  city?: string;
  postal_code?: string;
  limit: number;
  skip: number;
};

export async function getNpiProviders(
  params: GetNpiProvidersParams
): Promise<NpiProvidersResponse['results']> {
  try {
    const query = {
      state: params.state,
      city: params?.city,
      postal_code: params?.postal_code,
      limit: params?.limit.toString(),
      skip: params?.skip.toString(),
    };

    const response = await GET<NpiProvidersResponse>(ENDPOINTS.NPI_PROVIDERS, {
      query,
    });

    return response.results;
  } catch (error) {
    errorlog(
      error as Error,
      'src/api/npi-preferred/getNpiProviders',
      'Error fetching NPI providers'
    );
    return Promise.reject(toError(error));
  }
}

export async function getPreferredProvidersBySite(siteId: string): Promise<NpiProviderPreferred> {
  try {
    const response = await GET<{ data: NpiProviderPreferred }>(ENDPOINTS.NPI_PREFERRED, {
      query: { site_id: siteId },
    });

    return response.data;
  } catch (error) {
    errorlog(
      error as Error,
      'src/api/npi-preferred/getPreferredProvidersBySite',
      'Error fetching preferred NPI providers'
    );
    return Promise.reject(toError(error));
  }
}

export type SaveNpiProviderParams = {
  site_id: string;
  npi_number: string;
};

export async function saveNpiProvider(params: SaveNpiProviderParams) {
  try {
    const response = await POST<{ preferred_npi: string[] }>(ENDPOINTS.NPI_PREFERRED, params);
    return response;
  } catch (error) {
    errorlog(
      error as Error,
      'src/api/npi-preferred/saveNpiProvider',
      'Error saving preferred NPI provider'
    );
    return Promise.reject(toError(error));
  }
}

export type DeleteNpiProviderParams = {
  site_id: string;
  npi_number: string;
};

export async function deleteNpiProvider(params: DeleteNpiProviderParams) {
  try {
    await DELETE<{ preferred_npi: string[] }>(ENDPOINTS.NPI_PREFERRED, {
      query: params,
    });
  } catch (error) {
    errorlog(
      error as Error,
      'src/api/npi-preferred/deleteNpiProvider',
      'Error deleting preferred NPI provider'
    );
    return Promise.reject(toError(error));
  }
}
